// src/pages/actividades/ActividadesPage.tsx
import { useState, useRef, useMemo, useCallback, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui';
import { Plus, List, CalendarDays, CalendarRange, CalendarClock, Inbox, Upload, Repeat, ChevronDown, Pause, Play, Eye, EyeOff, CheckCircle2, X, CornerDownLeft, Sun, CalendarSearch, AlertTriangle, Search, Archive, Star, CalendarCheck } from 'lucide-react';
import { formatDate, cn } from '@/lib/utils';
import { useActividades, useCompletarActividad, useReabrirActividad, useActividadesContadores, useSmartQuickCreate, usePlantillasRecurrentes, usePausarPlantilla, useReactivarPlantilla, useBulkActualizarActividades, useBulkCompletarActividades, useBulkEliminarActividades, useReordenarActividades, useReparentActividad, useActualizarActividad, useLimpiezaDiaria } from '@/hooks/queries/useActividades';
import { useTimerActivo, useIniciarTimer, usePausarTimer, useReanudarTimer } from '@/hooks/queries/useTiempo';
import { useScreenState } from '@/hooks/useScreenState';
import { actividadScreenDef } from '@/screen-definitions';
import { ActividadFormModal } from './components/ActividadFormModal';
import { BulkActionBar } from './components/BulkActionBar';
import { ActividadDnDList } from './components/ActividadDnDList';
import { SeccionesHoy } from './components/SeccionesHoy';
import { AgendaHeader } from './components/agenda/AgendaHeader';
import { AgendaTimeGrid } from './components/agenda/AgendaTimeGrid';
import { CalendarioMes, getMonthQueryRange } from './components/CalendarioMes';
import { actividadesService } from '@/services/actividades';
import { parseActividad, type ParsedActividad } from '@/lib/semanticParser';
import { smartParse, mergeSmartIntoSemantic, type SmartParseResult } from '@/lib/smartParser';
import { useCatalogIndex } from '@/hooks/useCatalogIndex';
import { describirPatron } from '@/lib/recurrencia';
import { toast } from '@/components/ui';
import { useLocalStorage } from '@/hooks/common/useLocalStorage';
import { useIsMobile } from '@/hooks/useIsMobile';
import { ActividadesMobile } from './components/mobile/ActividadesMobile';
import type { ActividadListItem, ActividadFiltros, ActividadTab, AreaActividad, EstadoActividad, PrioridadActividad, AgendaViewMode } from '@/types';

const PRIORITY_ORDER: Record<string, number> = { AAA: 0, AA: 1, A: 2, B: 3, C: 4 };

function getTodayISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function getYesterdayISO(): string {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function getTomorrowISO(): string {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function get7DaysISO(): string {
  const d = new Date();
  d.setDate(d.getDate() + 7);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function formatDateISO(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function getMonday(d: Date): Date {
  const date = new Date(d);
  const day = date.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  date.setDate(date.getDate() + diff);
  date.setHours(0, 0, 0, 0);
  return date;
}

function getSunday(d: Date): Date {
  const monday = getMonday(d);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  return sunday;
}

function getWeekDays(d: Date): Date[] {
  const monday = getMonday(d);
  return Array.from({ length: 7 }, (_, i) => {
    const day = new Date(monday);
    day.setDate(monday.getDate() + i);
    return day;
  });
}

function getCurrentISOWeek(): string {
  const d = new Date();
  // ISO week: week containing the first Thursday of the year
  const target = new Date(d.valueOf());
  const dayNr = (d.getDay() + 6) % 7; // Monday = 0
  target.setDate(target.getDate() - dayNr + 3); // nearest Thursday
  const jan4 = new Date(target.getFullYear(), 0, 4);
  const dayDiff = (target.getTime() - jan4.getTime()) / 86400000;
  const weekNr = 1 + Math.round((dayDiff - 3 + ((jan4.getDay() + 6) % 7)) / 7);
  return `${target.getFullYear()}-W${String(weekNr).padStart(2, '0')}`;
}

const TAB_CONFIG: { key: ActividadTab; label: string; icon: React.ElementType }[] = [
  { key: 'atrasadas', label: 'Atrasadas', icon: AlertTriangle },
  { key: 'hoy', label: 'Hoy', icon: CalendarDays },
  { key: 'manana', label: 'Mañana', icon: Sun },
  { key: 'proximos7', label: '7 días', icon: CalendarRange },
  { key: 'esta_semana', label: 'Semana', icon: CalendarCheck },
  { key: 'todas', label: 'Todas', icon: List },
  { key: 'sin_fecha', label: 'Sin fecha', icon: Inbox },
  { key: 'algun_dia', label: 'Algún día', icon: Archive },
  { key: 'terminadas', label: 'Terminadas', icon: CheckCircle2 },
];

const VIEW_TOGGLE: { key: AgendaViewMode; label: string; icon: React.ElementType }[] = [
  { key: 'list', label: 'Lista', icon: List },
  { key: 'day', label: 'Día', icon: CalendarDays },
  { key: 'week', label: 'Semana', icon: CalendarRange },
  { key: 'month', label: 'Mes', icon: CalendarClock },
];

export function ActividadesPage() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const screenState = useScreenState(actividadScreenDef);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [viewMode, setViewMode] = useLocalStorage<AgendaViewMode>('agenda-view-mode', 'list');
  const [activeTab, setActiveTab] = useState<ActividadTab>('hoy');
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());

  // Quick add state for agenda
  const [quickAddDefaults, setQuickAddDefaults] = useState<{ fecha_agendada?: string; hora_agendada?: string } | null>(null);

  const { mutate: completarMutate } = useCompletarActividad();
  const { mutate: reabrirMutate } = useReabrirActividad();
  const { data: contadores } = useActividadesContadores();
  const smartCreate = useSmartQuickCreate();
  const { data: plantillas } = usePlantillasRecurrentes();
  const pausarPlantilla = usePausarPlantilla();
  const reactivarPlantilla = useReactivarPlantilla();
  // Timer
  const { data: timerData } = useTimerActivo();
  const iniciarTimer = useIniciarTimer();
  const pausarTimer = usePausarTimer();
  const reanudarTimer = useReanudarTimer();
  // Bulk actions
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const bulkUpdate = useBulkActualizarActividades();
  const bulkComplete = useBulkCompletarActividades();
  const bulkDelete = useBulkEliminarActividades();
  const isBulkPending = bulkUpdate.isPending || bulkComplete.isPending || bulkDelete.isPending;

  const [hideCompleted, setHideCompleted] = useLocalStorage('actividades-hide-completed', true);
  const [plantillasOpen, setPlantillasOpen] = useLocalStorage('plantillas-section-open', false);
  // DnD state
  const reordenar = useReordenarActividades();
  const reparent = useReparentActividad();
  const actualizarActividad = useActualizarActividad();
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [subtareasMap, setSubtareasMap] = useState<Record<string, ActividadListItem[]>>({});
  const [specificDate, setSpecificDate] = useState<string>('');
  const [quickTitle, setQuickTitle] = useState('');
  const quickInputRef = useRef<HTMLInputElement>(null);
  const catalogIndex = useCatalogIndex();
  // Destructure stable Fuse refs for useMemo deps (catalogIndex object changes every render)
  const { clientes: fuseClientes, contactos: fuseContactos, tiposActividad: fuseTipos, isLoading: catalogLoading } = catalogIndex;
  const [hasAutoSwitchedTab, setHasAutoSwitchedTab] = useState(false);
  const [soloEnfoque, setSoloEnfoque] = useState(false);

  // Limpieza diaria: reset en_foco de ayer y limpiar bloques viejos (once per session, retries on failure)
  const limpiezaDiaria = useLimpiezaDiaria();
  const limpiezaDone = useRef(false);
  useEffect(() => {
    if (!limpiezaDone.current) {
      limpiezaDone.current = true;
      limpiezaDiaria.mutate(undefined, {
        onError: () => {
          limpiezaDone.current = false; // Allow retry on next render
        },
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load subtareas on expand
  const handleLoadSubtareas = useCallback(async (id: string) => {
    try {
      const subs = await actividadesService.getSubtareas(id);
      setSubtareasMap((prev) => ({ ...prev, [id]: subs }));
    } catch {
      toast.error('Error al cargar subtareas');
    }
  }, []);

  const handleToggleExpand = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  // Auto-switch from "Hoy" to "Todas" if today has 0 activities
  const contadoresHoy = contadores?.hoy ?? -1;
  const contadoresTodas = contadores?.todas ?? 0;
  if (!hasAutoSwitchedTab && activeTab === 'hoy' && contadoresHoy === 0 && contadoresTodas > 0) {
    setActiveTab('todas');
    setHasAutoSwitchedTab(true);
  }

  // Split on // for subtasks: first part = parent, rest = subtask titles
  const { parentText, subtareaTitles } = useMemo(() => {
    const parts = quickTitle.split('//').map((s) => s.trim()).filter(Boolean);
    return {
      parentText: parts[0] ?? '',
      subtareaTitles: parts.slice(1),
    };
  }, [quickTitle]);

  // Live parse for chip preview (semantic + smart fuzzy) — only parse parent part
  const { parsedQuick, smartResult } = useMemo<{ parsedQuick: ParsedActividad | null; smartResult: SmartParseResult | null }>(() => {
    if (!parentText) return { parsedQuick: null, smartResult: null };
    const semantic = parseActividad(parentText);

    if (catalogLoading) {
      return { parsedQuick: semantic, smartResult: null };
    }

    const smart = smartParse(semantic.titulo, {
      clientes: fuseClientes,
      contactos: fuseContactos,
      tiposActividad: fuseTipos,
    });
    const merged = mergeSmartIntoSemantic(semantic, smart);
    return { parsedQuick: merged, smartResult: smart };
  }, [parentText, catalogLoading, fuseClientes, fuseContactos, fuseTipos]);

  // True if the parser found any semantic or smart-detected codes
  const hasSemanticCodes = parsedQuick && (
    parsedQuick.clienteRef || parsedQuick.contactoRef || parsedQuick.tipoActividadCodigo ||
    parsedQuick.proyectoRef || parsedQuick.ubicacionRef || parsedQuick.etiquetaRefs.length > 0 ||
    parsedQuick.fechaAgendada || parsedQuick.fechaLimite || parsedQuick.area ||
    parsedQuick.prioridad !== 'A' || parsedQuick.horaAgendada
  );
  const hasPreviewInfo = hasSemanticCodes || subtareaTitles.length > 0;

  const MAX_TITULO = 500;

  const handleQuickCreate = () => {
    if (!parentText) return;

    if (parentText.length > MAX_TITULO) {
      toast.warning(`El titulo excede ${MAX_TITULO} caracteres (tiene ${parentText.length}). Se recorto — presione Enter para crear.`);
      setQuickTitle(parentText.slice(0, MAX_TITULO));
      return;
    }

    const semantic = parseActividad(parentText);
    let merged = semantic;
    let smartIds: { clienteId?: string; contactoId?: string } = {};
    if (!catalogLoading) {
      const smart = smartParse(semantic.titulo, {
        clientes: fuseClientes,
        contactos: fuseContactos,
        tiposActividad: fuseTipos,
      });
      // Only merge high-confidence matches into the create data
      // "suggest" matches are shown as preview chips but NOT sent to the API
      const highConfSmart = {
        ...smart,
        clienteRef: smart.clienteMatch?.confidence === 'high' ? smart.clienteRef : undefined,
        clienteId: smart.clienteMatch?.confidence === 'high' ? smart.clienteId : undefined,
        contactoRef: smart.contactoMatch?.confidence === 'high' ? smart.contactoRef : undefined,
        contactoId: smart.contactoMatch?.confidence === 'high' ? smart.contactoId : undefined,
        tipoActividadId: smart.tipoActividadMatch?.confidence === 'high' ? smart.tipoActividadId : undefined,
        tipoActividadRef: smart.tipoActividadMatch?.confidence === 'high' ? smart.tipoActividadRef : undefined,
      };
      merged = mergeSmartIntoSemantic(semantic, highConfSmart);
      // Capture IDs resolved by Fuse.js to bypass backend re-resolution
      if (highConfSmart.clienteId) smartIds.clienteId = highConfSmart.clienteId;
      if (highConfSmart.contactoId) smartIds.contactoId = highConfSmart.contactoId;
    }

    // If parser stripped all codes and titulo is empty, build a descriptive title
    // from the parsed fields instead of falling back to raw text with codes
    let titulo = merged.titulo;
    if (!titulo) {
      const parts: string[] = [];
      if (merged.tipoActividadCodigo) parts.push(merged.tipoActividadCodigo);
      if (merged.clienteRef) parts.push(merged.clienteRef);
      if (merged.contactoRef) parts.push(`@${merged.contactoRef}`);
      titulo = parts.join(' ') || parentText;
    }

    smartCreate.mutate(
      {
        titulo,
        area: merged.area,
        prioridad: merged.prioridad,
        clienteRef: merged.clienteRef,
        contactoRef: merged.contactoRef,
        tipoCodigo: merged.tipoActividadCodigo,
        proyectoRef: merged.proyectoRef,
        ubicacionRef: merged.ubicacionRef,
        etiquetaRefs: merged.etiquetaRefs.length > 0 ? merged.etiquetaRefs : undefined,
        fechaAgendada: merged.fechaAgendada ? formatDateISO(merged.fechaAgendada) : undefined,
        horaAgendada: merged.horaAgendada,
        fechaLimite: merged.fechaLimite ? formatDateISO(merged.fechaLimite) : undefined,
        // Pre-resolved IDs from Fuse.js (skip backend re-resolution)
        resolvedClienteId: smartIds.clienteId,
        resolvedContactoId: smartIds.contactoId,
        // Subtasks from // separator
        subtareas: subtareaTitles.length > 0 ? subtareaTitles : undefined,
      },
      {
        onSuccess: ({ warnings }) => {
          if (warnings.length > 0) {
            toast.warning(warnings.join('. '));
          }
          const subCount = subtareaTitles.length;
          if (subCount > 0) {
            toast.success(`Actividad creada con ${subCount} subtarea${subCount > 1 ? 's' : ''}`);
          }
          setQuickTitle('');
          quickInputRef.current?.focus();
        },
        onError: () => {
          // Toast is shown by the mutation's global onError handler.
          // Keep input text so user can retry or edit.
          quickInputRef.current?.focus();
        },
      }
    );
  };

  // Force "Sin fecha", "Terminadas", and "Fecha específica" to list view
  const effectiveViewMode = (activeTab === 'atrasadas' || activeTab === 'sin_fecha' || activeTab === 'terminadas' || activeTab === 'fecha_especifica' || activeTab === 'algun_dia' || activeTab === 'esta_semana') ? 'list' : viewMode;

  const handleViewModeChange = (mode: AgendaViewMode) => {
    if (activeTab === 'atrasadas' || activeTab === 'sin_fecha' || activeTab === 'terminadas' || activeTab === 'fecha_especifica' || activeTab === 'algun_dia' || activeTab === 'esta_semana') return;
    setViewMode(mode);
  };

  const handleTabChange = (tab: ActividadTab) => {
    setActiveTab(tab);
    setSelectedIds([]);
    if (tab === 'hoy') {
      setSelectedDate(new Date());
    } else if (tab === 'manana') {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      setSelectedDate(tomorrow);
    }
  };

  // Navigation for agenda views
  const handlePrev = useCallback(() => {
    if (activeTab === 'hoy') setActiveTab('todas');
    setSelectedDate((prev) => {
      const d = new Date(prev);
      if (effectiveViewMode === 'month') {
        d.setMonth(d.getMonth() - 1);
      } else {
        d.setDate(d.getDate() - (effectiveViewMode === 'week' ? 7 : 1));
      }
      return d;
    });
  }, [effectiveViewMode, activeTab]);

  const handleNext = useCallback(() => {
    if (activeTab === 'hoy') setActiveTab('todas');
    setSelectedDate((prev) => {
      const d = new Date(prev);
      if (effectiveViewMode === 'month') {
        d.setMonth(d.getMonth() + 1);
      } else {
        d.setDate(d.getDate() + (effectiveViewMode === 'week' ? 7 : 1));
      }
      return d;
    });
  }, [effectiveViewMode, activeTab]);

  const handleToday = useCallback(() => {
    setSelectedDate(new Date());
    setActiveTab('hoy');
  }, []);

  // Quick add from agenda slot click
  const handleSlotClick = useCallback((date: Date, time: string) => {
    setQuickAddDefaults({ fecha_agendada: formatDateISO(date), hora_agendada: time });
    setIsModalOpen(true);
  }, []);

  // Build query filters based on active tab + view mode
  const queryFiltros = useMemo<ActividadFiltros>(() => {
    const areas = screenState.quickFilterValues.area;
    const estados = Array.isArray(screenState.filterValues.estado) ? screenState.filterValues.estado as string[] : undefined;
    const fecha = screenState.filterValues.fecha as { from?: string; to?: string } | undefined;

    const base: ActividadFiltros = {
      search: screenState.search || undefined,
      area: areas?.length === 1 ? areas[0] as AreaActividad : undefined,
      estado: estados?.length === 1 ? estados[0] as EstadoActividad : undefined,
      prioridad: screenState.filterValues.prioridad as PrioridadActividad | undefined,
      // Exclude backlog from all tabs except "algun_dia" and "terminadas"
      es_backlog: activeTab === 'algun_dia' ? true : (activeTab === 'terminadas' ? undefined : false),
    };

    // For agenda views, date range comes from selectedDate
    if (effectiveViewMode === 'month') {
      const { fecha_desde, fecha_hasta } = getMonthQueryRange(selectedDate);
      return { ...base, fecha_desde, fecha_hasta };
    }

    if (effectiveViewMode === 'day') {
      const dateStr = formatDateISO(selectedDate);
      return { ...base, fecha_desde: dateStr, fecha_hasta: dateStr };
    }

    if (effectiveViewMode === 'week') {
      const monday = getMonday(selectedDate);
      const sunday = getSunday(selectedDate);
      return { ...base, fecha_desde: formatDateISO(monday), fecha_hasta: formatDateISO(sunday) };
    }

    // List mode: use tab-level date filters
    switch (activeTab) {
      case 'atrasadas':
        return { ...base, fecha_hasta: getYesterdayISO() };
      case 'hoy':
        return { ...base, fecha_desde: getTodayISO(), fecha_hasta: getTodayISO(), incluir_subtareas_en_bloques: true };
      case 'manana':
        return { ...base, fecha_desde: getTomorrowISO(), fecha_hasta: getTomorrowISO() };
      case 'proximos7':
        return { ...base, fecha_desde: getTodayISO(), fecha_hasta: get7DaysISO() };
      case 'sin_fecha':
        return { ...base, sin_fecha: true };
      case 'algun_dia':
        return { ...base, es_backlog: true };
      case 'esta_semana':
        return { ...base, compromiso_semana: getCurrentISOWeek() };
      case 'terminadas':
        return { ...base, estado: 'COMPLETADA' as EstadoActividad };
      case 'fecha_especifica':
        return specificDate ? { ...base, fecha_desde: specificDate, fecha_hasta: specificDate } : base;
      case 'todas':
      default:
        return { ...base, fecha_desde: fecha?.from, fecha_hasta: fecha?.to };
    }
  }, [activeTab, effectiveViewMode, selectedDate, specificDate, screenState.search, screenState.quickFilterValues, screenState.filterValues]);

  const query = useActividades(queryFiltros);

  // Reload expanded subtareas when main query refetches (e.g. after completing a subtarea)
  useEffect(() => {
    if (query.dataUpdatedAt && expandedIds.size > 0) {
      for (const id of expandedIds) {
        actividadesService.getSubtareas(id).then((subs) => {
          setSubtareasMap((prev) => ({ ...prev, [id]: subs }));
        }).catch((err) => {
          console.error(`[Actividades] Failed to refresh subtareas for ${id}:`, err);
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.dataUpdatedAt]);

  // Check if user explicitly set an estado filter
  const hasExplicitEstadoFilter = Array.isArray(screenState.filterValues.estado)
    ? (screenState.filterValues.estado as string[]).length > 0
    : !!screenState.filterValues.estado;

  // Post-process query data: sort "Hoy" tab + exclude completed/cancelled by toggle
  const processedData = useMemo(() => {
    const data = query.data as ActividadListItem[] | undefined;
    if (!data) return data;

    let filtered = data;

    // Exclude COMPLETADA/CANCELADA when toggle is ON and no explicit estado filter
    // Never hide completed on the "Terminadas" tab
    if (hideCompleted && !hasExplicitEstadoFilter && activeTab !== 'terminadas') {
      filtered = filtered.filter(
        (a) => a.estado !== 'COMPLETADA' && a.estado !== 'CANCELADA'
      );
    }

    // "Solo enfoque" filter (only on Hoy tab)
    if (soloEnfoque && activeTab === 'hoy') {
      filtered = filtered.filter((a) => a.en_foco);
    }

    // For "Hoy"/"Mañana" tab in list: sort by manual orden (drag & drop), then priority as tiebreaker
    if ((activeTab === 'hoy' || activeTab === 'manana') && effectiveViewMode === 'list') {
      filtered = [...filtered].sort((a, b) => {
        const aCompleted = a.estado === 'COMPLETADA' || a.estado === 'CANCELADA';
        const bCompleted = b.estado === 'COMPLETADA' || b.estado === 'CANCELADA';

        if (aCompleted !== bCompleted) return aCompleted ? 1 : -1;

        // Respect manual order (drag & drop) first
        const ordenA = a.orden ?? 999;
        const ordenB = b.orden ?? 999;
        if (ordenA !== ordenB) return ordenA - ordenB;

        // Tiebreaker: priority
        const priA = PRIORITY_ORDER[a.prioridad] ?? 99;
        const priB = PRIORITY_ORDER[b.prioridad] ?? 99;
        if (priA !== priB) return priA - priB;

        return 0;
      });
    }

    return filtered;
  }, [query.data, activeTab, effectiveViewMode, hasExplicitEstadoFilter, hideCompleted, soloEnfoque]);

  // Filter subtareas by hideCompleted toggle
  const filteredSubtareasMap = useMemo(() => {
    if (!hideCompleted || activeTab === 'terminadas') return subtareasMap;
    const filtered: Record<string, ActividadListItem[]> = {};
    for (const [parentId, subs] of Object.entries(subtareasMap)) {
      filtered[parentId] = subs.filter(
        (s) => s.estado !== 'COMPLETADA' && s.estado !== 'CANCELADA'
      );
    }
    return filtered;
  }, [subtareasMap, hideCompleted, activeTab]);

  // Toggle focus star
  const handleToggleFoco = useCallback((id: string) => {
    const items = (query.data ?? []) as ActividadListItem[];
    let item = items.find((i) => i.id === id);
    // Also check subtareas (not in main list)
    if (!item) {
      for (const subs of Object.values(subtareasMap)) {
        item = subs.find((s) => s.id === id);
        if (item) break;
      }
    }
    if (!item) return;
    actualizarActividad.mutate({ id, data: { en_foco: !item.en_foco }, silent: true });
  }, [query.data, subtareasMap, actualizarActividad]);

  // Compute days for agenda view
  const agendaDays = useMemo(() => {
    if (effectiveViewMode === 'week') return getWeekDays(selectedDate);
    return [selectedDate];
  }, [effectiveViewMode, selectedDate]);

  const handleModalClose = () => {
    setIsModalOpen(false);
    setQuickAddDefaults(null);
  };

  // ── Mobile View ──
  if (isMobile) {
    return (
      <ActividadesMobile
        actividades={(processedData ?? []) as ActividadListItem[]}
        isLoading={query.isLoading}
        activeTab={activeTab}
        onTabChange={handleTabChange}
        contadores={contadores as Record<string, number> | undefined}
        soloEnfoque={soloEnfoque}
        onToggleEnfoque={() => setSoloEnfoque(!soloEnfoque)}
        onCompletarActividad={(id) => completarMutate({ id })}
        onReabrirActividad={(id) => reabrirMutate(id)}
        hideCompleted={hideCompleted}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Actividades</h1>
          <p className="text-muted-foreground">Gestiona tus tareas y actividades</p>
        </div>
        <div className="flex items-center gap-2">
          {/* View mode toggle */}
          <div className="flex bg-muted rounded-lg p-1">
            {VIEW_TOGGLE.map(({ key, label, icon: Icon }) => {
              const isDisabled = (activeTab === 'sin_fecha' || activeTab === 'terminadas' || activeTab === 'fecha_especifica' || activeTab === 'algun_dia') && key !== 'list';
              return (
                <button
                  key={key}
                  onClick={() => handleViewModeChange(key)}
                  disabled={isDisabled}
                  className={cn(
                    'px-3 py-1.5 rounded text-sm flex items-center gap-1 transition-colors',
                    effectiveViewMode === key
                      ? 'bg-background shadow text-foreground'
                      : 'text-muted-foreground hover:text-foreground',
                    isDisabled && 'opacity-50 cursor-not-allowed'
                  )}
                  title={isDisabled ? 'Sin fecha no tiene vista de agenda' : undefined}
                >
                  <Icon className="h-4 w-4" />
                  {label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Quick capture input with semantic parser */}
      <div>
        <div className="relative flex items-center">
          <input
            ref={quickInputRef}
            type="text"
            value={quickTitle}
            onChange={(e) => setQuickTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleQuickCreate();
              }
              if (e.key === 'Escape') {
                setQuickTitle('');
              }
            }}
            placeholder="Agregar actividad... (CLI:cliente @contacto FEC:fecha // subtarea1 // subtarea2)"
            className="w-full h-10 px-3 pr-20 rounded-md border border-input bg-background text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:opacity-50"
            disabled={smartCreate.isPending}
            autoComplete="off"
          />
          {quickTitle && (
            <div className="absolute right-2 flex items-center gap-1">
              <button
                type="button"
                onClick={() => { setQuickTitle(''); quickInputRef.current?.focus(); }}
                className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                title="Limpiar (Esc)"
              >
                <X className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={handleQuickCreate}
                disabled={smartCreate.isPending}
                className="p-1 rounded text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors disabled:opacity-50"
                title="Crear actividad (Enter)"
              >
                <CornerDownLeft className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
        {/* Semantic chips preview — shown when codes or subtasks are detected */}
        {hasPreviewInfo && parsedQuick && (
          <div className="flex items-center gap-1.5 mt-1 ml-1 flex-wrap">
            {parsedQuick.clienteRef && (
              <span className="inline-flex items-center rounded-full bg-blue-100/60 dark:bg-blue-900/30 px-2 py-0.5 text-xs text-blue-700 dark:text-blue-300">
                {parsedQuick.clienteRef}
                {smartResult?.clienteMatch?.confidence === 'suggest' && (
                  <span className="ml-1 text-amber-500 font-bold" title="Coincidencia aproximada">?</span>
                )}
              </span>
            )}
            {parsedQuick.contactoRef && (
              <span className="inline-flex items-center rounded-full bg-green-100/60 dark:bg-green-900/30 px-2 py-0.5 text-xs text-green-700 dark:text-green-300">
                @{parsedQuick.contactoRef}
                {smartResult?.contactoMatch?.confidence === 'suggest' && (
                  <span className="ml-1 text-amber-500 font-bold" title="Coincidencia aproximada">?</span>
                )}
              </span>
            )}
            {parsedQuick.tipoActividadCodigo && (
              <span className="inline-flex items-center rounded-full bg-orange-100/60 dark:bg-orange-900/30 px-2 py-0.5 text-xs text-orange-700 dark:text-orange-300">
                {parsedQuick.tipoActividadCodigo}
                {smartResult?.tipoActividadMatch?.confidence === 'suggest' && (
                  <span className="ml-1 text-amber-500 font-bold" title="Coincidencia aproximada">?</span>
                )}
              </span>
            )}
            {parsedQuick.fechaAgendada && (
              <span className="inline-flex items-center rounded-full bg-purple-100/60 dark:bg-purple-900/30 px-2 py-0.5 text-xs text-purple-700 dark:text-purple-300">
                {parsedQuick.fechaAgendada.toLocaleDateString('es-MX', { weekday: 'short', day: 'numeric', month: 'short' })}
                {parsedQuick.horaAgendada && ` ${parsedQuick.horaAgendada}`}
              </span>
            )}
            {parsedQuick.fechaLimite && (
              <span className="inline-flex items-center rounded-full bg-pink-100/60 dark:bg-pink-900/30 px-2 py-0.5 text-xs text-pink-700 dark:text-pink-300">
                Lim: {parsedQuick.fechaLimite.toLocaleDateString('es-MX', { day: 'numeric', month: 'short' })}
              </span>
            )}
            {parsedQuick.proyectoRef && (
              <span className="inline-flex items-center rounded-full bg-cyan-100/60 dark:bg-cyan-900/30 px-2 py-0.5 text-xs text-cyan-700 dark:text-cyan-300">
                +{parsedQuick.proyectoRef}
              </span>
            )}
            {parsedQuick.ubicacionRef && (
              <span className="inline-flex items-center rounded-full bg-gray-100/60 dark:bg-gray-800/30 px-2 py-0.5 text-xs text-gray-600 dark:text-gray-400">
                EN:{parsedQuick.ubicacionRef}
              </span>
            )}
            {parsedQuick.etiquetaRefs.map((t) => (
              <span key={t} className="inline-flex items-center rounded-full bg-gray-100/60 dark:bg-gray-800/30 px-2 py-0.5 text-xs text-gray-600 dark:text-gray-400">
                #{t}
              </span>
            ))}
            {parsedQuick.prioridad !== 'A' && (
              <span className={cn(
                'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
                parsedQuick.prioridad === 'AAA' ? 'bg-red-100/60 dark:bg-red-900/30 text-red-700 dark:text-red-300' : 'bg-orange-100/60 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300'
              )}>
                {parsedQuick.prioridad}
              </span>
            )}
            {!parsedQuick.fechaAgendada && parsedQuick.horaAgendada && (
              <span className="inline-flex items-center rounded-full bg-purple-100/60 dark:bg-purple-900/30 px-2 py-0.5 text-xs text-purple-700 dark:text-purple-300">
                {parsedQuick.horaAgendada}
              </span>
            )}
            {parsedQuick.area && (
              <span className={cn(
                'inline-flex items-center rounded-full px-2 py-0.5 text-xs',
                parsedQuick.area === 'TRABAJO' && 'bg-blue-100/60 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
                parsedQuick.area === 'ADMINISTRATIVO' && 'bg-amber-100/60 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
                parsedQuick.area === 'PERSONAL' && 'bg-emerald-100/60 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
              )}>
                {{ TRABAJO: 'Work', ADMINISTRATIVO: 'Admin', PERSONAL: 'Personal' }[parsedQuick.area]}
              </span>
            )}
            {subtareaTitles.length > 0 && (
              <span className="inline-flex items-center gap-1 rounded-full bg-indigo-100/60 dark:bg-indigo-900/30 px-2 py-0.5 text-xs text-indigo-700 dark:text-indigo-300" title={subtareaTitles.join('\n')}>
                <List className="h-3 w-3" />
                {subtareaTitles.length} subtarea{subtareaTitles.length > 1 ? 's' : ''}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Tab bar — shown for all view modes */}
      <div className="flex items-center gap-1 border-b">
        {TAB_CONFIG.map(({ key, label, icon: Icon }) => {
          const count = contadores?.[key as keyof typeof contadores] ?? null;
          const isActive = activeTab === key;
          const isOverdue = key === 'atrasadas' && (count ?? 0) > 0;
          // Hide atrasadas tab when count is 0
          if (key === 'atrasadas' && count === 0) return null;
          return (
            <button
              key={key}
              onClick={() => handleTabChange(key)}
              className={cn(
                'flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px',
                isActive
                  ? isOverdue ? 'border-destructive text-destructive' : 'border-primary text-primary'
                  : isOverdue ? 'border-transparent text-destructive/70 hover:text-destructive hover:border-destructive/30'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground/30'
              )}
            >
              <Icon className="h-4 w-4" />
              {label}
              {count !== null && (
                <span className={cn(
                  'ml-1 text-xs rounded-full px-1.5 py-0.5 min-w-[1.25rem] text-center',
                  isOverdue
                    ? 'bg-destructive/10 text-destructive'
                    : isActive
                    ? 'bg-primary/10 text-primary'
                    : 'bg-muted text-muted-foreground'
                )}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
        {/* Fecha específica — date picker tab */}
        <div className="flex items-center gap-1 ml-1">
          <button
            onClick={() => {
              if (activeTab !== 'fecha_especifica') {
                if (!specificDate) setSpecificDate(getTodayISO());
                setActiveTab('fecha_especifica');
                setSelectedIds([]);
              }
            }}
            className={cn(
              'flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 transition-colors -mb-px',
              activeTab === 'fecha_especifica'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground/30'
            )}
          >
            <CalendarSearch className="h-4 w-4" />
            Fecha
          </button>
          {activeTab === 'fecha_especifica' && (
            <input
              type="date"
              value={specificDate}
              onChange={(e) => setSpecificDate(e.target.value)}
              className="h-7 px-2 text-sm border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring -mb-px"
            />
          )}
        </div>
        {/* Toggle: Solo enfoque — only visible on Hoy tab */}
        {activeTab === 'hoy' && (
          <button
            type="button"
            onClick={() => setSoloEnfoque(!soloEnfoque)}
            className={cn(
              'ml-auto flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-full border transition-colors',
              soloEnfoque
                ? 'border-amber-400 bg-amber-50 text-amber-700 dark:border-amber-600 dark:bg-amber-950/30 dark:text-amber-400'
                : 'border-muted-foreground/20 text-muted-foreground hover:text-foreground hover:border-muted-foreground/40'
            )}
            title={soloEnfoque ? 'Mostrar todas' : 'Solo actividades en enfoque'}
          >
            <Star className={cn('h-3.5 w-3.5', soloEnfoque && 'fill-current')} />
            {soloEnfoque ? 'Solo enfoque' : 'Enfoque'}
          </button>
        )}
        {/* Toggle: show/hide completed — hidden on Terminadas tab */}
        {activeTab !== 'terminadas' && <button
          type="button"
          onClick={() => setHideCompleted(!hideCompleted)}
          className={cn(
            'ml-auto flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-full border transition-colors',
            hideCompleted
              ? 'border-muted-foreground/20 text-muted-foreground hover:text-foreground hover:border-muted-foreground/40'
              : 'border-green-300 bg-green-50 text-green-700 dark:border-green-700 dark:bg-green-950/30 dark:text-green-400'
          )}
          title={hideCompleted ? 'Mostrar completadas y canceladas' : 'Ocultar completadas y canceladas'}
        >
          {hideCompleted ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
          {hideCompleted ? 'Terminadas ocultas' : 'Mostrando todas'}
        </button>}
      </div>

      {/* Bulk action bar */}
      {selectedIds.length > 0 && (
        <BulkActionBar
          count={selectedIds.length}
          isPending={isBulkPending}
          isBacklogTab={activeTab === 'algun_dia'}
          onMoveDate={(fecha) => {
            bulkUpdate.mutate({ ids: selectedIds, fecha_agendada: fecha, es_backlog: false }, {
              onSuccess: () => setSelectedIds([]),
            });
          }}
          onChangePriority={(prioridad) => {
            bulkUpdate.mutate({ ids: selectedIds, prioridad }, {
              onSuccess: () => setSelectedIds([]),
            });
          }}
          onChangeArea={(area) => {
            bulkUpdate.mutate({ ids: selectedIds, area }, {
              onSuccess: () => setSelectedIds([]),
            });
          }}
          onMoveToBacklog={() => {
            bulkUpdate.mutate({ ids: selectedIds, es_backlog: true }, {
              onSuccess: () => setSelectedIds([]),
            });
          }}
          onRemoveFromBacklog={() => {
            bulkUpdate.mutate({ ids: selectedIds, es_backlog: false }, {
              onSuccess: () => setSelectedIds([]),
            });
          }}
          onCompleteAll={() => {
            bulkComplete.mutate({ ids: selectedIds }, {
              onSuccess: () => setSelectedIds([]),
            });
          }}
          onDeleteAll={() => {
            bulkDelete.mutate({ ids: selectedIds }, {
              onSuccess: () => setSelectedIds([]),
            });
          }}
          onClearSelection={() => setSelectedIds([])}
        />
      )}

      {/* Main content */}
      {effectiveViewMode === 'list' ? (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {processedData && processedData.length > 0 && (
                <input
                  type="checkbox"
                  checked={selectedIds.length > 0 && selectedIds.length === processedData.length}
                  ref={(el) => {
                    if (el) el.indeterminate = selectedIds.length > 0 && selectedIds.length < processedData.length;
                  }}
                  onChange={() => {
                    if (selectedIds.length === processedData.length) {
                      setSelectedIds([]);
                    } else {
                      setSelectedIds(processedData.map((a) => a.id));
                    }
                  }}
                  className="h-3.5 w-3.5 rounded border-gray-300 text-primary focus:ring-primary/50 cursor-pointer"
                  title={selectedIds.length === processedData.length ? 'Deseleccionar todas' : 'Seleccionar todas'}
                />
              )}
              <p className="text-xs text-muted-foreground">
                {processedData?.length ?? 0} actividades
                {processedData && processedData.length > 0 && ' · Arrastra para reordenar'}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {/* Search filter */}
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Buscar actividades..."
                  value={screenState.search}
                  onChange={(e) => screenState.setSearch(e.target.value)}
                  className="h-8 w-48 pl-8 pr-7 rounded-md border border-input bg-background text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1"
                />
                {screenState.search && (
                  <button
                    type="button"
                    onClick={() => screenState.setSearch('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
              <Button variant="outline" size="sm" onClick={() => navigate('/actividades/importar')}>
                <Upload className="h-4 w-4 mr-2" />
                Importar
              </Button>
              <Button size="sm" onClick={() => setIsModalOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Nueva Actividad
              </Button>
            </div>
          </div>
          {query.isLoading ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground text-sm">
              Cargando actividades...
            </div>
          ) : activeTab === 'hoy' ? (
            <SeccionesHoy
              items={processedData ?? []}
              onReorder={(items) => reordenar.mutate(items)}
              onReparent={(id, padreId) => {
                reparent.mutate({ id, data: { padre_id: padreId } }, {
                  onSuccess: () => {
                    if (padreId) {
                      setExpandedIds((prev) => new Set([...prev, padreId]));
                      handleLoadSubtareas(padreId);
                      toast.success('Actividad movida como subtarea');
                    } else {
                      toast.success('Actividad movida a nivel raíz');
                    }
                  },
                });
              }}
              onComplete={(id) => completarMutate({ id })}
              onReopen={(id) => reabrirMutate(id)}
              onStartTimer={(id) => iniciarTimer.mutate(id)}
              onPauseTimer={() => pausarTimer.mutate()}
              onResumeTimer={() => reanudarTimer.mutate()}
              isTimerPaused={timerData?.pausado}
              onUpdatePriority={(id, prioridad) => actualizarActividad.mutate({ id, data: { prioridad }, silent: true })}
              onToggleFoco={handleToggleFoco}
              onAssignBloque={(actividadId, bloqueId) => actualizarActividad.mutate({ id: actividadId, data: { bloque_id: bloqueId }, silent: true })}
              timerActividadId={timerData?.actividad_id ?? undefined}
              isTimerActive={timerData?.activo}
              expandedIds={expandedIds}
              onToggleExpand={handleToggleExpand}
              subtareasMap={filteredSubtareasMap}
              onLoadSubtareas={handleLoadSubtareas}
              selectedIds={selectedIds}
              onToggleSelect={(id) => {
                setSelectedIds((prev) =>
                  prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
                );
              }}
            />
          ) : (
            <ActividadDnDList
              items={processedData ?? []}
              onReorder={(items) => reordenar.mutate(items)}
              onReparent={(id, padreId) => {
                reparent.mutate({ id, data: { padre_id: padreId } }, {
                  onSuccess: () => {
                    if (padreId) {
                      setExpandedIds((prev) => new Set([...prev, padreId]));
                      handleLoadSubtareas(padreId);
                      toast.success('Actividad movida como subtarea');
                    } else {
                      toast.success('Actividad movida a nivel raíz');
                    }
                  },
                });
              }}
              onComplete={(id) => completarMutate({ id })}
              onReopen={(id) => reabrirMutate(id)}
              onStartTimer={(id) => iniciarTimer.mutate(id)}
              onPauseTimer={() => pausarTimer.mutate()}
              onResumeTimer={() => reanudarTimer.mutate()}
              isTimerPaused={timerData?.pausado}
              onUpdatePriority={(id, prioridad) => actualizarActividad.mutate({ id, data: { prioridad }, silent: true })}
              onToggleFoco={handleToggleFoco}
              timerActividadId={timerData?.actividad_id ?? undefined}
              isTimerActive={timerData?.activo}
              expandedIds={expandedIds}
              onToggleExpand={handleToggleExpand}
              subtareasMap={filteredSubtareasMap}
              onLoadSubtareas={handleLoadSubtareas}
              selectedIds={selectedIds}
              onToggleSelect={(id) => {
                setSelectedIds((prev) =>
                  prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
                );
              }}
            />
          )}
        </div>
      ) : effectiveViewMode === 'month' ? (
        <CalendarioMes
          currentDate={selectedDate}
          onDateChange={setSelectedDate}
          actividades={processedData ?? []}
          isLoading={query.isLoading}
          onClickActividad={(id) => navigate(`/actividades/${id}`)}
          onViewDay={(date) => {
            setSelectedDate(date);
            setViewMode('day');
          }}
          onCreateActividad={(fecha) => {
            setQuickAddDefaults({ fecha_agendada: fecha });
            setIsModalOpen(true);
          }}
          onReschedule={(id, newDate) => {
            actualizarActividad.mutate({ id, data: { fecha_agendada: newDate }, silent: true });
          }}
        />
      ) : (
        <div className="space-y-2">
          <AgendaHeader
            selectedDate={selectedDate}
            viewMode={effectiveViewMode as 'day' | 'week'}
            onPrev={handlePrev}
            onNext={handleNext}
            onToday={handleToday}
          />
          {query.isLoading ? (
            <div className="border rounded-lg bg-background flex items-center justify-center py-20">
              <div className="text-sm text-muted-foreground">Cargando actividades...</div>
            </div>
          ) : (
            <AgendaTimeGrid
              days={agendaDays}
              activities={(query.data as ActividadListItem[] | undefined) ?? []}
              onSlotClick={handleSlotClick}
            />
          )}
        </div>
      )}

      {/* Collapsible: Recurrencias activas */}
      {plantillas && plantillas.length > 0 && (
        <div className="border rounded-lg">
          <button
            type="button"
            onClick={() => setPlantillasOpen(!plantillasOpen)}
            className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
          >
            <div className="flex items-center gap-2">
              <Repeat className="h-4 w-4" />
              Recurrencias activas ({plantillas.length})
            </div>
            <ChevronDown className={cn('h-4 w-4 transition-transform', plantillasOpen && 'rotate-180')} />
          </button>
          {plantillasOpen && (
            <div className="border-t divide-y">
              {plantillas.map((p) => (
                <div
                  key={p.id}
                  className={cn(
                    'flex items-center justify-between px-4 py-2.5 text-sm',
                    !p.activo && 'opacity-50'
                  )}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <Repeat className="h-3.5 w-3.5 text-blue-500 shrink-0" />
                    <div className="min-w-0">
                      <Link
                        to={`/actividades/${p.id}`}
                        className="font-medium hover:text-primary truncate block"
                      >
                        {p.titulo}
                      </Link>
                      <span className="text-xs text-muted-foreground">
                        {describirPatron(p)}
                        {p.proxima_ocurrencia && ` · Próxima: ${formatDate(p.proxima_ocurrencia)}`}
                      </span>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => p.activo ? pausarPlantilla.mutate(p.id) : reactivarPlantilla.mutate(p.id)}
                    className={cn(
                      'shrink-0 p-1.5 rounded hover:bg-muted transition-colors',
                      p.activo ? 'text-muted-foreground hover:text-orange-600' : 'text-muted-foreground hover:text-green-600'
                    )}
                    title={p.activo ? 'Pausar recurrencia' : 'Reactivar recurrencia'}
                  >
                    {p.activo ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <ActividadFormModal
        open={isModalOpen}
        onClose={handleModalClose}
        defaultValues={quickAddDefaults ?? undefined}
      />
    </div>
  );
}
